'use client'

import { Fragment, useState } from 'react'
import { format } from 'date-fns'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { SelectedOption } from '@cerifi/ecomm/bigcommerce/types/cart'
import {
  CheckboxOption,
  DateFieldOption,
  MultiLineTextFieldOption,
  NumberFieldOption,
  Product,
  ProductOption,
  TextFieldOption,
} from '@cerifi/ecomm/bigcommerce/types/product'
import useAccountStore from '@cerifi/state/account-store'
import useNotificationStore from '@cerifi/state/notification.store'

import { Button } from '../button'
import { Checkbox } from '../checkbox'
import { Currency } from '../currency'
import { Divider } from '../divider'
import { Input } from '../input'
import { RadioGroup, RadioGroupItem } from '../radio'
import { Select, SelectItem } from '../select'
import { Textarea } from '../textarea'

const ProductModifiers = ({ product }: { product: Product }) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm()

  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const addItemToCart = useAccountStore((state) => state.addItemToCart)
  const showToast = useNotificationStore((state) => state.showToast)

  // runs when form is submitted and valid
  const onSubmit: SubmitHandler<any> = (data) => {
    if (isAddingToCart) {
      return
    }

    // converting form data into data that bigcommerce can understand for product modifiers
    const optionsSelected: SelectedOption[] = Object.keys(data)
      .map((fieldName) => {
        return {
          id: parseInt(
            `${product.options.find((x) => x.displayName === fieldName)?.id}`,
            10
          ),
          name: fieldName,
          value:
            product.options.find((x) => x.displayName === fieldName)
              ?.__typename === 'DateFieldOption' && data[fieldName]
              ? new Date(data[fieldName]).toString()
              : data[fieldName],
        }
      })
      .filter((x) => !!x.value)

    setIsAddingToCart(true)
    addItemToCart({
      product,
      quantity: 1,
      variantId: product.variants[0]?.id || '',
      optionsSelected,
    })
      .finally(() => setIsAddingToCart(false))
      .then(() => {
        showToast({
          title: 'Added to cart',
          level: 'success',
        })
      })
      .catch(() => {
        showToast({
          title: 'Something went wrong',
          description: 'please try again',
          level: 'error',
        })
      })
  }

  const renderOption = (o: ProductOption) => {
    switch (o.__typename) {
      case 'MultipleChoiceOption':
        switch (o.displayStyle) {
          case 'RadioButtons':
            return renderRadio(o)
          case 'DropdownList':
          default:
            return renderMultiChoice(o)
        }
      case 'TextFieldOption':
        return renderTextField(o as TextFieldOption)
      case 'CheckboxOption':
        return renderCheckboxField(o as CheckboxOption)
      case 'NumberFieldOption':
        return renderNumberField(o as NumberFieldOption)
      case 'DateFieldOption':
        return renderDateField(o as DateFieldOption)
      case 'MultiLineTextFieldOption':
        return renderMultiLineTextFieldOption(o as MultiLineTextFieldOption)
      default:
        throw new Error(
          'unknown product modifier from BigCommerce. Front end does not know how to handle it'
        )
    }
  }

  const renderMultiChoice = (o: ProductOption) => (
    <div>
      <Controller
        control={control}
        name={o.displayName}
        rules={{
          required: o.isRequired ? 'Required' : false,
        }}
        render={({ field: { value, onChange, onBlur } }) => (
          <Select
            label={o.displayName}
            invalid={!!errors[o.displayName]}
            errorMessage={(errors[o.displayName]?.message as string) || ''}
            onBlur={onBlur}
            onValueChange={(v) => onChange(parseInt(v, 10))}
            value={`${value}`}
          >
            {o.values.map((v) => (
              <SelectItem key={v.entityId} value={`${v.entityId}`}>
                {v.label}
              </SelectItem>
            ))}
          </Select>
        )}
      />
    </div>
  )

  const renderRadio = (o: ProductOption) => (
    <div>
      <Controller
        control={control}
        name={o.displayName}
        rules={{
          required: o.isRequired ? 'Required' : false,
        }}
        render={({ field: { value, onChange, onBlur } }) => (
          <RadioGroup
            label={o.displayName}
            onBlur={onBlur}
            value={value}
            onValueChange={onChange}
            invalid={!!errors[o.displayName]}
            errorMessage={(errors[o.displayName]?.message as string) || ''}
          >
            {o.values.map((v) => (
              <RadioGroupItem value={`${v.entityId}`} key={v.entityId}>
                {v.label}
              </RadioGroupItem>
            ))}
          </RadioGroup>
        )}
      ></Controller>
    </div>
  )

  const renderTextField = (o: TextFieldOption) => (
    <Input
      label={o.displayName}
      invalid={!!errors[o.displayName]}
      errorMessage={(errors[o.displayName]?.message as string) || ''}
      {...register(o.displayName, {
        required: o.isRequired ? 'Required' : false,
        minLength: o.minLength
          ? {
              value: o.minLength,
              message: `min length ${o.minLength}`,
            }
          : undefined,
        maxLength: o.maxLength
          ? {
              value: o.maxLength,
              message: `max length ${o.maxLength}`,
            }
          : undefined,
      })}
    />
  )

  const renderNumberField = (o: NumberFieldOption) => (
    <Input
      label={o.displayName}
      invalid={!!errors[o.displayName]}
      errorMessage={(errors[o.displayName]?.message as string) || ''}
      type="number"
      step={o.isIntegerOnly ? '1' : '0.01'}
      {...register(o.displayName, {
        required: o.isRequired ? 'Required' : false,
        min: o.lowest
          ? {
              value: o.lowest,
              message: `min value is ${o.lowest}`,
            }
          : undefined,
        max: o.highest
          ? {
              value: o.highest,
              message: `max value is ${o.highest}`,
            }
          : undefined,
      })}
    />
  )

  const renderDateField = (o: DateFieldOption) => (
    <Input
      label={o.displayName}
      invalid={!!errors[o.displayName]}
      type="date"
      errorMessage={(errors[o.displayName]?.message as string) || ''}
      {...register(o.displayName, {
        required: o.isRequired ? 'Required' : false,
        min: o.earliest
          ? {
              value: o.earliest,
              message: `min date is ${format(
                new Date(o.earliest),
                'MM/dd/yyyy'
              )}`,
            }
          : undefined,
        max: o.latest
          ? {
              value: o.latest,
              message: `max date is ${format(
                new Date(o.latest),
                'MM/dd/yyyy'
              )}`,
            }
          : undefined,
      })}
    />
  )

  const renderCheckboxField = (o: CheckboxOption) => (
    <div>
      <Controller
        control={control}
        name={o.displayName}
        rules={{
          required: o.isRequired ? 'Required' : false,
        }}
        render={({ field: { value, onChange, onBlur } }) => (
          <div>
            <label className="font-semibold mb-1 block">{o.displayName}</label>
            <Checkbox
              onBlur={onBlur}
              aria-label={o.displayName}
              checked={value}
              onCheckedChange={onChange}
              invalid={!!errors.myCheckbox}
              errorMessage={(errors.myCheckbox?.message as string) || ''}
            >
              <div className="text-base font-semibold cursor-pointer">
                {o.label}
              </div>
            </Checkbox>
          </div>
        )}
      />
    </div>
  )

  const renderMultiLineTextFieldOption = (o: MultiLineTextFieldOption) => (
    <Textarea
      label={o.displayName}
      invalid={!!errors[o.displayName]}
      errorMessage={(errors[o.displayName]?.message as string) || ''}
      {...register(o.displayName, {
        required: o.isRequired ? 'Required' : false,
        minLength: o.minLength
          ? {
              value: o.minLength,
              message: `min length ${o.minLength}`,
            }
          : undefined,
        maxLength: o.maxLength
          ? {
              value: o.maxLength,
              message: `max length ${o.maxLength}`,
            }
          : undefined,
      })}
    />
  )

  return (
    <form
      className="border border-tan-150 py-8 px-11 max-w-[25.2rem] mx-auto"
      aria-label={product.name + ' add to cart'}
      onSubmit={handleSubmit(onSubmit as any)}
    >
      <div className="text-xl font-medium">{product.name}</div>

      <div className="text-base font-bold mt-1">
        <Currency dollars={product.price.value} />
      </div>

      <div className="flex flex-col gap-9 mt-9">
        {product.options.map((o) => (
          <Fragment key={o.id}>{renderOption(o)}</Fragment>
        ))}
      </div>

      <Divider className="mt-9 mb-6" />

      <Button isLoading={isAddingToCart} className="w-full">
        Add to Cart
      </Button>
    </form>
  )
}

export default ProductModifiers
